import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { X, Check, Loader2, Image as ImageIcon, Instagram, Mail, Video, Globe, ShoppingCart, LayoutGrid, Facebook, Youtube, Twitter, Newspaper, MousePointer2, Tag, Heart, Sparkles, ThumbsUp, ThumbsDown, Search, RefreshCw } from 'lucide-react';
import imageGenerationService, { ImageStyle, PlatformType, ImageStyleCategoryUser, RecommendedStyle } from '../../services/api/imageGenerationService';
import { pickRandomDistinctStyles } from '../../services/api/autoStyleService';
import ThrottledImage from '../common/ThrottledImage';
import { buildStyleBrowsingComparator } from '../../utils/styleBrowsingOrder';

export interface SelectedStyle {
  id: number;
  name: string;
  stylePrompt: string;
  aspectRatio: string;
  category: string;
  platforms?: string[];  // Platform array (e.g., ['instagram', 'facebook'])
  // Format-specific fields (from keisiki.md)
  cta?: string;       // CTA presence (e.g., "yes", "no")
  feature?: string;   // Platform-specific feature description
  // Material count constraints (used to trim reference images on the client)
  minMaterialCount?: number;
  maxMaterialCount?: number | null;
  // Dominant tone of the style's background. null/undefined = not classified.
  backgroundTone?: 'light' | 'dark' | null;
  // Sample image of the style (list API: example_image_url / auto-select API: thumbnail_url)
  exampleImageUrl?: string | null;
}

// Custom fetch functions for different style types (e.g., material styles)
export interface StyleFetchFunctions {
  getStyles: () => Promise<{ success: boolean; data: ImageStyle[] }>;
  getStyleCategories: () => Promise<ImageStyleCategoryUser[]>;
}

interface StyleSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedStyle: SelectedStyle | null) => void;
  onSelectMany?: (selectedStyles: SelectedStyle[]) => void;
  currentStyleId?: number | null;
  currentStyleIds?: number[];
  multiSelect?: boolean;
  maxSelection?: number;
  fetchFunctions?: StyleFetchFunctions;
  productId?: number | null;
  /** モジュールスコープ SWR キャッシュのキー（banner / carousel など）。再オープン時の即時表示に使う。 */
  cacheKey?: string;
}

// Category display config with colors (slug -> color mapping)
// These colors are used for visual distinction
const CATEGORY_COLORS: Record<string, string> = {
  sns: '#E1306C',
  marketing: '#3B82F6',
  web: '#22C55E',
  ec: '#A855F7',
  other: '#6B7280',
};

// Default color for categories not in the mapping
const DEFAULT_CATEGORY_COLOR = '#6B7280';

const EMPTY_CURRENT_STYLE_IDS: number[] = [];

const DEFAULT_SEARCH_TAGS = ['手書き', '自然', '現場感', '高級感', 'SNS風'];

// Platform configuration with icons and colors
const PLATFORM_CONFIG: Record<PlatformType, { label: string; icon: React.ReactNode; color: string }> = {
  instagram: { label: 'Instagram', icon: <Instagram size={14} />, color: '#E1306C' },
  facebook: { label: 'Facebook', icon: <Facebook size={14} />, color: '#1877F2' },
  youtube: { label: 'YouTube', icon: <Youtube size={14} />, color: '#FF0000' },
  x: { label: 'X', icon: <Twitter size={14} />, color: '#000000' },
  tiktok: { label: 'TikTok', icon: <Video size={14} />, color: '#000000' },
  line: { label: 'LINE', icon: <Mail size={14} />, color: '#00C300' },
  yahoo_banner: { label: 'Yahoo!広告', icon: <MousePointer2 size={14} />, color: '#FF0033' },
  google_banner: { label: 'Google広告', icon: <MousePointer2 size={14} />, color: '#4285F4' },
  smartnews: { label: 'SmartNews', icon: <Newspaper size={14} />, color: '#FF6600' },
  website: { label: 'Webサイト', icon: <Globe size={14} />, color: '#22C55E' },
  mailmag: { label: 'メルマガ', icon: <Mail size={14} />, color: '#6366F1' },
};

// スタイルモーダルの取得データをモジュールスコープに保持する SWR キャッシュ。
// 再オープン時はここから即時反映してスピナーを出さず、裏で silent revalidate する。
type StyleModalCacheEntry = {
  styles: ImageStyle[];
  categories: ImageStyleCategoryUser[];
  recommendations: RecommendedStyle[];
  recommendReason: string;
  /** 推薦が商品から決めた業種（商品なし・決まらないときは null） */
  recommendedCategory: string | null;
};

const styleModalCache = new Map<string, StyleModalCacheEntry>();
const recommendationDisplayHistory = new Map<string, number[]>();
const RECOMMENDATION_DISPLAY_COUNT = 6;
const RECOMMENDATION_CANDIDATE_LIMIT = 18;

const chooseRecommendationPage = (
  key: string,
  candidates: RecommendedStyle[],
): RecommendedStyle[] => {
  const selected = pickRandomDistinctStyles(
    candidates,
    RECOMMENDATION_DISPLAY_COUNT,
    recommendationDisplayHistory.get(key) ?? [],
  );
  recommendationDisplayHistory.set(key, selected.map((style) => style.id));
  return selected;
};

const buildStyleModalCacheKey = (cacheKey: string, productId?: number | null): string =>
  `${cacheKey}:${productId ?? 'all'}`;

const loadStyleModalData = async (
  fetchFunctions?: StyleFetchFunctions,
  productId?: number | null
): Promise<StyleModalCacheEntry> => {
  if (fetchFunctions) {
    const [stylesResponse, categoriesData] = await Promise.all([
      fetchFunctions.getStyles(),
      fetchFunctions.getStyleCategories(),
    ]);

    if (!stylesResponse.success) {
      throw new Error('スタイルの取得に失敗しました');
    }

    return {
      styles: stylesResponse.data,
      categories: categoriesData,
      recommendations: [],
      recommendReason: '',
      recommendedCategory: null,
    };
  }

  const params = {
    ...(productId ? { product_id: productId } : {}),
    limit: RECOMMENDATION_CANDIDATE_LIMIT,
  };
  const [stylesResponse, categoriesData, recsResponse] = await Promise.all([
    imageGenerationService.getStyles(),
    imageGenerationService.getStyleCategories(),
    imageGenerationService.getStyleRecommendations(params),
  ]);

  if (!stylesResponse.success) {
    throw new Error('スタイルの取得に失敗しました');
  }

  return {
    styles: stylesResponse.data,
    categories: categoriesData,
    recommendations: recsResponse.success && recsResponse.data ? recsResponse.data.recommended : [],
    recommendReason: recsResponse.success && recsResponse.data ? recsResponse.data.reason : '',
    recommendedCategory: recsResponse.success && recsResponse.data ? recsResponse.data.category_slug ?? null : null,
  };
};

/**
 * ページマウント時などに事前取得してモジュールキャッシュを温める（初回オープンの即時表示用）。
 * best-effort。失敗しても握りつぶす。
 */
export const prefetchStyleModalData = async (
  cacheKey: string,
  fetchFunctions?: StyleFetchFunctions,
  productId?: number | null
): Promise<void> => {
  const key = buildStyleModalCacheKey(cacheKey, productId);
  if (styleModalCache.has(key)) {
    return;
  }
  try {
    styleModalCache.set(key, await loadStyleModalData(fetchFunctions, productId));
  } catch {
    // プリフェッチは失敗しても無視（オープン時に本取得される）
  }
};

const StyleSelectModal: React.FC<StyleSelectModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  onSelectMany,
  currentStyleId,
  currentStyleIds = EMPTY_CURRENT_STYLE_IDS,
  multiSelect = false,
  maxSelection,
  fetchFunctions,
  productId,
  cacheKey,
}) => {
  const [styles, setStyles] = useState<ImageStyle[]>([]);
  const [categories, setCategories] = useState<ImageStyleCategoryUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<ImageStyle | null>(null);
  const [selectedStyles, setSelectedStyles] = useState<ImageStyle[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [togglingStyleId, setTogglingStyleId] = useState<number | null>(null);
  const [ratingStyleId, setRatingStyleId] = useState<number | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendedStyle[]>([]);
  const [recommendationCandidates, setRecommendationCandidates] = useState<RecommendedStyle[]>([]);
  const [recommendReason, setRecommendReason] = useState<string>('');
  const [productCategory, setProductCategory] = useState<string | null>(null);
  // 商品の業種タブへの自動切り替えは、開くたびに一度だけ。利用者がタブを選んだ後は切り替えない
  const autoCategoryDoneRef = useRef(false);

  // Fetch styles and categories when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchStylesAndCategories();
    } else {
      // Reset state when modal closes
      setActiveCategory('all');
      setSearchQuery('');
      setSelectedTag(null);
      setError(null);
      setProductCategory(null);
      autoCategoryDoneRef.current = false;
    }
  }, [isOpen]);

  // Set initial selected style based on currentStyleId
  // 開いた時（と親の選択値が変わった時）だけ初期化する。親の再描画で中身が同じ新しい配列が来たり、
  // 一覧の再取得で styles が差し替わったりするたびに初期化すると、選択途中のスタイルが消える。
  const currentStyleIdsKey = currentStyleIds.join(',');
  const initializedSelectionKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      initializedSelectionKeyRef.current = null;
      return;
    }
    if (styles.length === 0) {
      if (!multiSelect && !currentStyleId) setSelectedStyle(null);
      return;
    }
    const selectionKey = `${multiSelect}:${currentStyleId ?? ''}:${currentStyleIdsKey}`;
    if (initializedSelectionKeyRef.current === selectionKey) return;
    initializedSelectionKeyRef.current = selectionKey;

    if (multiSelect) {
      const ids = currentStyleIdsKey ? currentStyleIdsKey.split(',').map(Number) : [];
      setSelectedStyles(styles.filter(s => ids.includes(s.id)));
      setSelectedStyle(null);
    } else if (currentStyleId) {
      const style = styles.find(s => s.id === currentStyleId);
      if (style) {
        setSelectedStyle(style);
      }
    } else {
      setSelectedStyle(null);
    }
  }, [isOpen, currentStyleId, currentStyleIdsKey, multiSelect, styles]);

  const applyStyleModalData = (data: StyleModalCacheEntry) => {
    setStyles(data.styles);
    setCategories(data.categories);
    setRecommendationCandidates(data.recommendations);
    setRecommendations(chooseRecommendationPage(String(productId ?? 'all'), data.recommendations));
    setRecommendReason(data.recommendReason);
    setProductCategory(data.recommendedCategory);
  };

  const fetchStylesAndCategories = async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;

    // cacheKey 未指定のモーダルは従来どおり毎回取得（キャッシュ無効・回帰防止）。
    if (!cacheKey) {
      setLoading(true);
      setError(null);
      try {
        applyStyleModalData(await loadStyleModalData(fetchFunctions, productId));
      } catch (err) {
        console.error('Failed to fetch styles or categories:', err);
        setError('スタイルの取得に失敗しました');
      } finally {
        setLoading(false);
      }
      return;
    }

    const key = buildStyleModalCacheKey(cacheKey, productId);
    const cached = styleModalCache.get(key);

    // キャッシュがあれば即時反映（スピナー非表示）し、裏で最新化する。
    if (cached && !silent) {
      applyStyleModalData(cached);
      setError(null);
      void fetchStylesAndCategories({ silent: true });
      return;
    }

    if (!silent) {
      setLoading(true);
      setError(null);
    }

    try {
      const data = await loadStyleModalData(fetchFunctions, productId);
      styleModalCache.set(key, data);
      applyStyleModalData(data);
      if (!silent) {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch styles or categories:', err);
      if (!silent) {
        setError('スタイルの取得に失敗しました');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Build category config from fetched categories
  const categoryConfig = useMemo(() => {
    const config: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
      all: { label: 'すべて', icon: <LayoutGrid size={16} />, color: '#6B7280' },
    };

    categories.forEach(cat => {
      config[cat.slug] = {
        label: cat.name,
        icon: <Tag size={16} />,
        color: CATEGORY_COLORS[cat.slug] || DEFAULT_CATEGORY_COLOR,
      };
    });

    return config;
  }, [categories]);

  const handleStyleSelect = useCallback((style: ImageStyle) => {
    if (multiSelect) {
      setSelectedStyles(prev => {
        const isSelected = prev.some(s => s.id === style.id);
        if (isSelected) {
          return prev.filter(s => s.id !== style.id);
        }
        if (maxSelection && prev.length >= maxSelection) {
          return prev;
        }
        return [...prev, style];
      });
      return;
    }

    if (selectedStyle?.id === style.id) {
      // Deselect if clicking the same style
      setSelectedStyle(null);
    } else {
      setSelectedStyle(style);
    }
  }, [maxSelection, multiSelect, selectedStyle]);

  const toSelectedStyle = useCallback((style: ImageStyle): SelectedStyle => ({
    id: style.id,
    name: style.name,
    stylePrompt: style.style_prompt,
    aspectRatio: style.aspect_ratio,
    category: style.category,
    platforms: style.platforms,
    cta: style.cta,
    feature: style.feature,
    minMaterialCount: style.min_material_count,
    maxMaterialCount: style.max_material_count,
    backgroundTone: style.background_tone ?? null,
    exampleImageUrl: style.example_image_url ?? null,
  }), []);

  // 一覧 API は prompt_template を返さない（P1-3）。既定モーダル（fetchFunctions なし）では
  // 選択確定時に個別取得して stylePrompt を埋める。fetchFunctions 系は一覧に stylePrompt を
  // 含むためそのまま使う。
  const resolveSelectedStyle = useCallback(async (style: ImageStyle): Promise<SelectedStyle> => {
    const base = toSelectedStyle(style);
    if (fetchFunctions || base.stylePrompt) {
      return base;
    }
    try {
      const stylePrompt = await imageGenerationService.getStylePrompt(style.id);
      return { ...base, stylePrompt };
    } catch (err) {
      console.error('Failed to fetch style prompt:', err);
      return base;
    }
  }, [fetchFunctions, toSelectedStyle]);

  const handleConfirm = useCallback(async () => {
    if (multiSelect) {
      const resolved = await Promise.all(selectedStyles.map(resolveSelectedStyle));
      onSelectMany?.(resolved);
      onClose();
      return;
    }

    if (selectedStyle) {
      onSelect(await resolveSelectedStyle(selectedStyle));
    } else {
      onSelect(null);
    }
    onClose();
  }, [multiSelect, onClose, onSelect, onSelectMany, selectedStyle, selectedStyles, resolveSelectedStyle]);

  const handleToggleFavorite = useCallback(async (e: React.MouseEvent, styleId: number) => {
    e.stopPropagation();
    if (togglingStyleId === styleId) return;

    setTogglingStyleId(styleId);
    const currentState = styles.find(s => s.id === styleId)?.is_favorited ?? false;

    // Optimistic update
    setStyles(prev => prev.map(s =>
      s.id === styleId ? { ...s, is_favorited: !currentState } : s
    ));

    try {
      await imageGenerationService.toggleFavorite(styleId);
    } catch (err) {
      // Revert on failure
      setStyles(prev => prev.map(s =>
        s.id === styleId ? { ...s, is_favorited: currentState } : s
      ));
      console.error('Failed to toggle favorite:', err);
    } finally {
      setTogglingStyleId(null);
    }
  }, [styles, togglingStyleId]);

  const handleRateStyle = useCallback(async (
    e: React.MouseEvent,
    styleId: number,
    rating: 'good' | 'bad',
  ) => {
    e.stopPropagation();
    if (ratingStyleId === styleId || fetchFunctions) return;

    const currentStyle = styles.find(s => s.id === styleId);
    if (!currentStyle) return;

    const previousRating = currentStyle.my_rating ?? null;
    const previousSummary = currentStyle.ratings_summary ?? { good: 0, bad: 0 };
    const isRemoving = previousRating === rating;

    const optimisticSummary = { ...previousSummary };
    if (previousRating) {
      optimisticSummary[previousRating] = Math.max(0, optimisticSummary[previousRating] - 1);
    }
    if (!isRemoving) {
      optimisticSummary[rating] += 1;
    }

    setRatingStyleId(styleId);
    setStyles(prev => prev.map(s => (
      s.id === styleId
        ? {
            ...s,
            my_rating: isRemoving ? null : rating,
            my_rating_reason: null,
            ratings_summary: optimisticSummary,
          }
        : s
    )));

    try {
      const result = isRemoving
        ? await imageGenerationService.deleteStyleRating(styleId)
        : await imageGenerationService.rateStyle(styleId, rating, {
            context: {
              source: 'style_select_modal',
              product_id: productId ?? null,
            },
          });

      setStyles(prev => prev.map(s => (
        s.id === styleId
          ? {
              ...s,
              my_rating: result.rating,
              my_rating_reason: result.reason,
              ratings_summary: result.ratings_summary,
            }
          : s
      )));
      if (result.rating === 'bad') {
        setRecommendations(prev => prev.filter(rec => rec.id !== styleId));
      }
    } catch (err) {
      setStyles(prev => prev.map(s => (
        s.id === styleId
          ? {
              ...s,
              my_rating: previousRating,
              ratings_summary: previousSummary,
            }
          : s
      )));
      console.error('Failed to rate style:', err);
    } finally {
      setRatingStyleId(null);
    }
  }, [fetchFunctions, productId, ratingStyleId, styles]);

  const handleClear = useCallback(() => {
    if (multiSelect) {
      setSelectedStyles([]);
    } else {
      setSelectedStyle(null);
    }
  }, [multiSelect]);

  const getCategoryCount = useCallback((categorySlug: string) => {
    if (categorySlug === 'all') return styles.length;
    return styles.filter((style) => style.category === categorySlug).length;
  }, [styles]);

  const handleCategorySelect = useCallback((categorySlug: string) => {
    autoCategoryDoneRef.current = true;
    setActiveCategory(categorySlug);
  }, []);

  // 検索やタグを一度でも使ったら、消した後も自動で切り替えない
  useEffect(() => {
    if (searchQuery || selectedTag) autoCategoryDoneRef.current = true;
  }, [searchQuery, selectedTag]);

  // 商品を選んで開いたら、その商品の業種タブで開く。選択済みのスタイルがあるとき・
  // 検索やタグで絞っているときは「すべて」のまま（選んだスタイルが別タブに隠れないように）。
  // 描画前に切り替え、「すべて」が一瞬見えないようにする
  useLayoutEffect(() => {
    if (!isOpen || autoCategoryDoneRef.current || !productId || !productCategory) return;
    if (activeCategory !== 'all' || searchQuery || selectedTag) return;
    if (currentStyleId != null || currentStyleIds.length > 0 || selectedStyle || selectedStyles.length > 0) return;
    if (!categories.some((category) => category.slug === productCategory && category.styles_count > 0)) return;
    autoCategoryDoneRef.current = true;
    setActiveCategory(productCategory);
  }, [activeCategory, categories, currentStyleId, currentStyleIds, isOpen, productCategory, productId, searchQuery, selectedStyle, selectedStyles, selectedTag]);

  const filteredStyles = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const filteredByCategory = activeCategory === 'all'
      ? styles
      : styles.filter((style) => style.category === activeCategory);
    const filtered = filteredByCategory.filter((style) => {
      if (selectedTag && !(style.tags || []).includes(selectedTag)) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const searchableText = [
        style.name,
        style.description,
        style.style_prompt,
        style.category,
        style.subcategory_name,
        style.subcategory_slug,
        ...(style.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedQuery);
    });

    // 開いた時点で選択済みだったスタイル（props基準）を最優先。
    // モーダル内トグルのstateではなくpropsで判定し、クリックのたびに並びが飛ばないようにする。
    const currentlySelectedIds = new Set<number>(currentStyleIds);
    if (currentStyleId != null) {
      currentlySelectedIds.add(currentStyleId);
    }

    // Sort: currently selected first, then favorites, then categories mixed newest-first.
    // 素材・カルーセルなど独自の一覧（fetchFunctions）は、管理画面の表示順で返る並びを保つ
    const compareForBrowsing = fetchFunctions
      ? (a: ImageStyle, b: ImageStyle) => Number(Boolean(b.is_favorited)) - Number(Boolean(a.is_favorited))
        || (b.created_at || '').localeCompare(a.created_at || '')
      : buildStyleBrowsingComparator(filtered);
    return [...filtered].sort((a, b) => {
      const aSelected = currentlySelectedIds.has(a.id);
      const bSelected = currentlySelectedIds.has(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return compareForBrowsing(a, b);
    });
  }, [styles, activeCategory, searchQuery, selectedTag, currentStyleIds, currentStyleId]);

  const availableSearchTags = useMemo(() => {
    const tags = new Set(DEFAULT_SEARCH_TAGS);
    styles.forEach(style => (style.tags || []).forEach(tag => tags.add(tag)));
    return Array.from(tags).filter(tag => tag.trim().length > 0).slice(0, 12);
  }, [styles]);

  // Get available category keys (all + fetched categories)
  const categoryKeys = useMemo(() => {
    return ['all', ...categories.map(c => c.slug)];
  }, [categories]);

  const availableCategoryKeys = useMemo(() => {
    return categoryKeys.filter((categorySlug) => categorySlug === 'all' || getCategoryCount(categorySlug) > 0);
  }, [categoryKeys, getCategoryCount]);

  const getAspectRatioLabel = (ratio: string) => {
    switch (ratio) {
      case '1:1':
        return '正方形';
      case '4:5':
        return '縦長 (4:5)';
      case '9:16':
        return '縦長 (9:16)';
      case '16:9':
        return '横長 (16:9)';
      case '3:4':
        return '縦長 (3:4)';
      case '4:3':
        return '横長 (4:3)';
      default:
        return ratio;
    }
  };

  const isNewStyle = useCallback((createdAt: string) => {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays <= 7;
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6 max-[640px]:p-3" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-[1280px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] max-[640px]:max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between rounded-t-lg border-b border-b-gray-200 bg-[#fafafa] px-6 py-5 max-[768px]:p-4">
          <div className="flex flex-col items-start gap-1">
            <h2 className="m-0 flex items-center gap-2 text-[18px] font-semibold text-gray-800">スタイルを選ぶ</h2>
            <p className="m-0 text-[13px] text-gray-500">
              {multiSelect
                ? `用途に合わせたスタイルを複数選択できます${maxSelection ? `（最大${maxSelection}件）` : ''}`
                : '用途に合わせた最適なスタイルを選択してください'}
            </p>
          </div>
          <button className="flex h-9 w-9 items-center justify-center rounded-lg border-none bg-transparent text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex min-h-0 w-full max-w-full flex-1 flex-col overflow-y-auto overflow-x-hidden p-6 [scrollbar-color:#c1c1c1_#f1f1f1] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-track]:bg-[#f1f1f1] [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-[#c1c1c1] [&::-webkit-scrollbar-thumb:hover]:bg-[#a8a8a8] max-[768px]:p-4">
          {error && (
            <div className="mb-4 flex items-center justify-between gap-2 rounded-lg border border-[#fecaca] bg-destructive/10 p-6 text-center text-sm text-destructive [grid-column:1/-1]">
              <span>{error}</span>
              <button onClick={() => fetchStylesAndCategories()}>再試行</button>
            </div>
          )}

          <div className="mb-3 flex items-center gap-3 max-[640px]:flex-col max-[640px]:items-stretch">
            <label className="relative flex h-10 min-w-[240px] flex-1 items-center rounded-lg border border-gray-300 bg-white px-3 text-gray-500 max-[640px]:min-w-0">
              <Search size={16} />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="手書き・自然・現場感などで検索"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md border-0 bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  onClick={() => setSearchQuery('')}
                  aria-label="検索語をクリア"
                >
                  <X size={14} />
                </button>
              )}
            </label>
          </div>

          <div className="mb-3 flex flex-wrap gap-1.5" aria-label="検索タグ">
            {availableSearchTags.map(tag => (
              <button
                key={tag}
                type="button"
                className={
                    selectedTag === tag
                      ? 'inline-flex h-7 items-center rounded-full border px-2.5 text-xs font-medium transition-all border-blue-500 bg-blue-500 text-white'
                      : 'inline-flex h-7 items-center rounded-full border px-2.5 text-xs font-medium transition-all border-gray-300 bg-white text-gray-700 hover:border-[#93c5fd] hover:bg-[#eff6ff] hover:text-[#1d4ed8]'
                  }
                onClick={() => setSelectedTag(current => current === tag ? null : tag)}
              >
                {tag}
              </button>
            ))}
          </div>

          <div className="mb-2.5 inline-flex flex-wrap items-center gap-2 text-xs text-gray-500 [&>span]:rounded-md [&>span]:bg-gray-100 [&>span]:px-2 [&>span]:py-1 max-[640px]:justify-start">
            <span>{filteredStyles.length}件</span>
            <span>{Math.max(availableCategoryKeys.length - 1, 0)}カテゴリ</span>
            {(searchQuery || selectedTag) && (
              <button
                type="button"
                className="rounded-md border-0 bg-[#eff6ff] px-2 py-1 text-xs text-[#2563eb] hover:bg-[#dbeafe]"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedTag(null);
                }}
              >
                条件をクリア
              </button>
            )}
          </div>

          <div className="mb-4 flex flex-wrap gap-1.5">
            {availableCategoryKeys.map((categorySlug) => {
              const config = categoryConfig[categorySlug];
              if (!config) return null;
              const count = getCategoryCount(categorySlug);
              return (
                <button
                  key={categorySlug}
                  className={
                      activeCategory === categorySlug
                        ? 'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-[5px] text-[11px] font-medium transition-all [&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0 border-[var(--category-color,#3b82f6)] bg-[var(--category-color,#3b82f6)] text-white'
                        : 'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-[5px] text-[11px] font-medium transition-all [&>svg]:h-3 [&>svg]:w-3 [&>svg]:shrink-0 border-gray-200 bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }
                  onClick={() => handleCategorySelect(categorySlug)}
                  style={{
                    '--category-color': config.color,
                  } as React.CSSProperties}
                >
                  {config.icon}
                  {config.label}
                  <span className="rounded-full bg-gray-200 px-[5px] py-px text-[10px] text-gray-500 opacity-[0.62]">{count}</span>
                </button>
              );
            })}
          </div>

          {/* Recommended Styles */}
          {!loading && recommendations.length > 0 && activeCategory === 'all' && (
            <div className="mb-5 border-b border-b-gray-200 pb-5">
              <div className="mb-3 flex flex-wrap items-center gap-1.5 text-sm font-semibold text-[#7c3aed]">
                <Sparkles size={16} />
                <span>{recommendReason}</span>
                {recommendationCandidates.length > RECOMMENDATION_DISPLAY_COUNT && (
                  <button
                    type="button"
                    className="ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-[#ddd6fe] bg-white px-2 py-1 text-xs font-semibold text-[#7c3aed] hover:border-[#c4b5fd] hover:bg-[#f5f3ff]"
                    onClick={() => {
                      const recommendationKey = String(productId ?? 'all');
                      setRecommendations(chooseRecommendationPage(recommendationKey, recommendationCandidates));
                    }}
                  >
                    <RefreshCw size={13} />
                    入れ替える
                  </button>
                )}
              </div>
              <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(140px,1fr))]">
                {recommendations.map((rec) => {
                  const matchingStyle = styles.find(s => s.id === rec.id);
                  const isSelected = multiSelect
                    ? selectedStyles.some(s => s.id === rec.id)
                    : selectedStyle?.id === rec.id;
                  return (
                    <div
                      key={rec.id}
                      className={isSelected ? 'overflow-hidden rounded-xl border-2 bg-gray-50 cursor-pointer transition-all border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)] hover:-translate-y-0.5' : 'overflow-hidden rounded-xl border-2 bg-gray-50 cursor-pointer transition-all border-gray-200 hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'}
                      onClick={() => matchingStyle && handleStyleSelect(matchingStyle)}
                    >
                      <div className="relative block w-full">
                        {rec.thumbnail_url ? (
                          <ThrottledImage src={rec.thumbnail_url} alt={rec.name} wrapperClassName="style-thumb-wrap" imgClassName="style-thumb" />
                        ) : (
                          <div className="flex h-full min-h-[120px] w-full items-center justify-center text-gray-400">
                            <ImageIcon size={32} />
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center bg-blue-500/60 text-white">
                            <Check size={24} />
                          </div>
                        )}
                        <span className="absolute left-2 top-2 flex items-center gap-[3px] rounded-[10px] bg-[linear-gradient(135deg,#7c3aed,#a855f7)] px-2 py-0.5 text-[10px] font-semibold text-white">
                          <Sparkles size={10} /> おすすめ
                        </span>
                        {rec.recommendation_badge && (
                          <span className="absolute bottom-2 left-2 max-w-[calc(100%-16px)] overflow-hidden text-ellipsis whitespace-nowrap rounded-[10px] bg-[rgba(22,163,74,0.95)] px-2 py-0.5 text-[10px] font-semibold text-white">
                            {rec.recommendation_badge}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-col p-3">
                        <h3 className="m-0 mb-2 block w-full text-[13px] font-semibold leading-[1.3] text-gray-800">{rec.name}</h3>
                        {(rec.usage_count_30d || rec.favorite_count) ? (
                          <div className="text-[11px] leading-[1.4] text-gray-500">
                            {rec.usage_count_30d ? `30日利用 ${rec.usage_count_30d}件` : ''}
                            {rec.usage_count_30d && rec.favorite_count ? ' / ' : ''}
                            {rec.favorite_count ? `保存 ${rec.favorite_count}件` : ''}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Styles Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-gray-500">
              <Loader2 className="mb-4 animate-spin text-blue-500" size={32} />
              <p>スタイルを読み込み中...</p>
            </div>
          ) : filteredStyles.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-sm text-gray-400 [&>svg]:mb-4 [&>svg]:opacity-50">
              <ImageIcon size={48} />
              <p>
                {activeCategory === 'all'
                  ? '登録されているスタイルがありません'
                  : `${categoryConfig[activeCategory]?.label || activeCategory}カテゴリのスタイルがありません`}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(150px,1fr))] max-[640px]:grid-cols-1">
              {filteredStyles.map((style) => {
                const isSelected = multiSelect
                  ? selectedStyles.some(s => s.id === style.id)
                  : selectedStyle?.id === style.id;
                return (
                  <div
                    key={style.id}
                    className={isSelected ? 'overflow-hidden rounded-xl border-2 bg-gray-50 cursor-pointer transition-all border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.2)] hover:-translate-y-0.5' : 'overflow-hidden rounded-xl border-2 bg-gray-50 cursor-pointer transition-all border-gray-200 hover:-translate-y-0.5 hover:border-blue-500 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]'}
                    onClick={() => handleStyleSelect(style)}
                  >
                    <div className="relative block w-full">
                      {style.example_image_url ? (
                        <ThrottledImage src={style.example_image_url} alt={style.name} wrapperClassName="style-thumb-wrap" imgClassName="style-thumb" />
                      ) : (
                        <div className="flex h-full min-h-[120px] w-full items-center justify-center text-gray-400">
                          <ImageIcon size={32} />
                        </div>
                      )}
                      {isNewStyle(style.created_at) && (
                        <span className="absolute left-2 top-2 z-[2] rounded bg-red-500 px-2 py-0.5 text-[10px] font-bold tracking-[0.5px] text-white shadow-[0_1px_3px_rgba(0,0,0,0.15)]">NEW</span>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-blue-500/60 text-white">
                          <Check size={24} />
                        </div>
                      )}
                      <button
                        className={
                            style.is_favorited
                              ? 'absolute right-2 top-2 z-[2] flex h-8 w-8 items-center justify-center rounded-full border-none shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all hover:scale-110 bg-white text-red-500'
                              : 'absolute right-2 top-2 z-[2] flex h-8 w-8 items-center justify-center rounded-full border-none shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all hover:scale-110 bg-white/90 text-gray-400 hover:bg-white hover:text-red-500'
                          }
                        onClick={(e) => handleToggleFavorite(e, style.id)}
                        disabled={togglingStyleId === style.id}
                        title={style.is_favorited ? 'お気に入りから削除' : 'お気に入りに追加'}
                      >
                        <Heart size={16} fill={style.is_favorited ? 'currentColor' : 'none'} />
                      </button>
                      {!fetchFunctions && (
                        <div className="absolute bottom-2 right-2 z-[2] inline-flex gap-1" aria-label="スタイル評価">
                          <button
                            className={
                              style.my_rating === 'good'
                                ? 'inline-flex h-7 w-7 items-center justify-center rounded-full border-none shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all enabled:hover:scale-[1.08] enabled:hover:bg-white disabled:cursor-wait disabled:opacity-[0.65] bg-white text-[#16a34a]'
                                : 'inline-flex h-7 w-7 items-center justify-center rounded-full border-none shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all enabled:hover:scale-[1.08] enabled:hover:bg-white disabled:cursor-wait disabled:opacity-[0.65] bg-white/[0.92] text-gray-500 hover:text-[#16a34a]'
                            }
                            onClick={(e) => handleRateStyle(e, style.id, 'good')}
                            disabled={ratingStyleId === style.id}
                            title={style.my_rating === 'good' ? '高評価を取り消す' : 'このスタイルを高評価'}
                          >
                            <ThumbsUp size={14} />
                          </button>
                          <button
                            className={
                              style.my_rating === 'bad'
                                ? 'inline-flex h-7 w-7 items-center justify-center rounded-full border-none shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all enabled:hover:scale-[1.08] enabled:hover:bg-white disabled:cursor-wait disabled:opacity-[0.65] bg-white text-[#dc2626]'
                                : 'inline-flex h-7 w-7 items-center justify-center rounded-full border-none shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all enabled:hover:scale-[1.08] enabled:hover:bg-white disabled:cursor-wait disabled:opacity-[0.65] bg-white/[0.92] text-gray-500 hover:text-[#dc2626]'
                            }
                            onClick={(e) => handleRateStyle(e, style.id, 'bad')}
                            disabled={ratingStyleId === style.id}
                            title={style.my_rating === 'bad' ? '低評価を取り消す' : 'このスタイルを低評価'}
                          >
                            <ThumbsDown size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col p-3">
                      <h3 className="m-0 mb-2 block w-full text-[13px] font-semibold leading-[1.3] text-gray-800">{style.name}</h3>
                      {/* Platform icons - separate row */}
                      {style.platforms && style.platforms.length > 0 && (
                        <div className="mb-2 flex flex-wrap items-center gap-1.5">
                          {style.platforms.map((platform) => {
                            const config = PLATFORM_CONFIG[platform];
                            if (!config) return null;
                            return (
                              <span
                                key={platform}
                                className="inline-flex shrink-0 items-center justify-center [&>svg]:block"
                                style={{ color: config.color }}
                                title={config.label}
                              >
                                {config.icon}
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {/* Category and aspect ratio */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className="inline-block whitespace-nowrap rounded px-2 py-[3px] text-[11px] font-medium text-white"
                          style={{ backgroundColor: categoryConfig[style.category]?.color || DEFAULT_CATEGORY_COLOR }}
                        >
                          {categoryConfig[style.category]?.label || style.category}
                        </span>
                        {!fetchFunctions && (
                          <>
                            <span className="whitespace-nowrap rounded bg-gray-100 px-2 py-[3px] text-[11px] text-gray-500">
                              {getAspectRatioLabel(style.aspect_ratio)}
                            </span>
                            {style.cta && (
                              <span className={
                              style.cta === 'yes'
                                ? 'whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium bg-[#dcfce7] text-[#166534]'
                                : 'whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500'
                            }>
                                CTA{style.cta === 'yes' ? 'あり' : 'なし'}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-t-gray-200 bg-gray-50 px-6 py-4 max-[768px]:flex-col max-[768px]:p-4 max-[640px]:items-stretch max-[640px]:gap-3">
          {multiSelect && selectedStyles.length > 0 && (
            <div className="flex min-w-0 flex-1 items-start gap-3 max-[640px]:mb-3">
              <span className="shrink-0 text-[13px] text-gray-500">選択中:</span>
              <span className="text-sm font-medium text-gray-800">{selectedStyles.map(style => style.name).join('、')}</span>
              <button className="shrink-0 rounded border border-gray-300 bg-transparent px-2.5 py-1 text-xs text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800" onClick={handleClear}>
                クリア
              </button>
            </div>
          )}
          {!multiSelect && selectedStyle && (
            <div className="flex min-w-0 flex-1 items-center gap-3 max-[640px]:mb-3">
              <span className="shrink-0 text-[13px] text-gray-500">選択中:</span>
              <span className="text-sm font-medium text-gray-800">{selectedStyle.name}</span>
              <button className="shrink-0 rounded border border-gray-300 bg-transparent px-2.5 py-1 text-xs text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800" onClick={handleClear}>
                クリア
              </button>
            </div>
          )}
          <div className="flex shrink-0 gap-3 max-[640px]:w-full max-[640px]:justify-end">
            <button className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-800" onClick={onClose}>
              キャンセル
            </button>
            <button
              className="rounded-lg border-none bg-blue-500 px-6 py-2.5 text-sm font-medium text-white transition-all enabled:hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-400"
              onClick={handleConfirm}
            >
              {multiSelect
                ? selectedStyles.length > 0 ? `${selectedStyles.length}件を適用` : 'スタイルなしで続行'
                : selectedStyle ? '適用' : 'スタイルなしで続行'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleSelectModal;
